import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { FiRotateCcw, FiInbox } from "react-icons/fi";
import MobileSubNavigator from "../../../components/MobileSubNavigator";
import { getHomeLinks } from "../../../utils/homeLinks";
import {
  tokens,
  Button,
  SearchInput,
  Select,
  Badge,
  Tag,
  EmptyState,
  Pagination,
} from "../../../components/admin/AdminUI";
import { dummyLogs, logLevels, logCategories } from "./dummyData";

const c = tokens.color;
const PAGE_SIZE = 8;

const LEVEL_META = {
  INFO: { tone: "brand", label: "정보" },
  WARN: { tone: "warning", label: "경고" },
  ERROR: { tone: "danger", label: "오류" },
};

const Container = styled.div`
  width: 100%;
  max-width: 100vw;
  margin: 0;
  padding: 1rem;
  background: ${c.appBg};
  color: ${c.text};
  min-height: 100vh;
  box-sizing: border-box;
  overflow-x: hidden;
  font-family: ${tokens.font};
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.6rem;
  margin-bottom: 1rem;
`;

const StatCard = styled.div`
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.xs};
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const StatLabel = styled.span`
  font-size: 12px;
  color: ${c.muted};
`;

const StatValue = styled.span`
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${(p) => p.color || c.heading};
`;

const FilterCard = styled.div`
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.card};
  padding: 1rem;
  margin-bottom: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const LogCard = styled.div`
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.xs};
  padding: 1rem;
  margin-bottom: 0.6rem;
`;

const LogTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const LogTime = styled.span`
  font-size: 12px;
  color: ${c.muted};
  font-variant-numeric: tabular-nums;
`;

const LogAction = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: ${c.heading};
  margin-bottom: 8px;
`;

const LogMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px 12px;
  font-size: 13px;
  color: ${c.muted};
  margin-bottom: 8px;
`;

const LogDetail = styled.div`
  font-size: 13px;
  color: ${c.muted};
  background: ${c.surfaceMuted};
  border: 1px solid ${c.borderSubtle};
  border-radius: ${tokens.radius.sm};
  padding: 8px 10px;
`;

const ListCard = styled.div`
  background: ${c.surface};
  border: 1px solid ${c.border};
  border-radius: ${tokens.radius.lg};
  box-shadow: ${tokens.shadow.card};
  overflow: hidden;
`;

export default function MobileSystemLog() {
  const links = getHomeLinks();

  const [levelFilter, setLevelFilter] = useState("ALL");
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [searchValue, setSearchValue] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(1);

  const filteredLogs = useMemo(() => {
    const keyword = appliedSearch.trim().toLowerCase();
    return dummyLogs.filter((log) => {
      if (levelFilter !== "ALL" && log.level !== levelFilter) return false;
      if (categoryFilter !== "ALL" && log.category !== categoryFilter) return false;
      if (keyword) {
        const haystack = `${log.user} ${log.action} ${log.detail} ${log.ip}`.toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [levelFilter, categoryFilter, appliedSearch]);

  const totalPages = Math.max(1, Math.ceil(filteredLogs.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedLogs = filteredLogs.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  const stats = useMemo(
    () => ({
      total: dummyLogs.length,
      info: dummyLogs.filter((l) => l.level === "INFO").length,
      warn: dummyLogs.filter((l) => l.level === "WARN").length,
      error: dummyLogs.filter((l) => l.level === "ERROR").length,
    }),
    []
  );

  const handleSearch = () => {
    setAppliedSearch(searchValue);
    setPage(1);
  };

  const handleReset = () => {
    setLevelFilter("ALL");
    setCategoryFilter("ALL");
    setSearchValue("");
    setAppliedSearch("");
    setPage(1);
  };

  const isFiltered =
    levelFilter !== "ALL" || categoryFilter !== "ALL" || appliedSearch !== "";

  return (
    <MobileSubNavigator
      links={links}
      sectionTitle="시스템 로그"
      backPath="/"
      showBackButton={true}
    >
      <Container>
        <StatGrid>
          <StatCard>
            <StatLabel>전체 로그</StatLabel>
            <StatValue>{stats.total}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>정보</StatLabel>
            <StatValue color={c.primary}>{stats.info}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>경고</StatLabel>
            <StatValue color={c.warningText}>{stats.warn}</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>오류</StatLabel>
            <StatValue color={c.dangerText}>{stats.error}</StatValue>
          </StatCard>
        </StatGrid>

        <FilterCard>
          <SearchInput
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onSearch={handleSearch}
            placeholder="사용자, 활동, IP 검색"
            grow="1 1 100%"
          />
          <Select
            value={levelFilter}
            onChange={(v) => {
              setLevelFilter(v);
              setPage(1);
            }}
            options={logLevels}
            grow="1 1 100%"
            minWidth="100%"
          />
          <Select
            value={categoryFilter}
            onChange={(v) => {
              setCategoryFilter(v);
              setPage(1);
            }}
            options={logCategories}
            grow="1 1 100%"
            minWidth="100%"
          />
          <div style={{ display: "flex", gap: 8 }}>
            <Button $variant="primary" onClick={handleSearch} style={{ flex: 1 }}>
              검색
            </Button>
            {isFiltered && (
              <Button $variant="secondary" onClick={handleReset}>
                <FiRotateCcw /> 초기화
              </Button>
            )}
          </div>
        </FilterCard>

        {pagedLogs.length === 0 ? (
          <ListCard>
            <EmptyState
              icon={<FiInbox />}
              title="조회된 로그가 없습니다"
              description={
                isFiltered
                  ? "필터 조건과 일치하는 로그가 없습니다."
                  : "아직 수집된 시스템 로그가 없습니다."
              }
            />
          </ListCard>
        ) : (
          <>
            {pagedLogs.map((log) => {
              const meta = LEVEL_META[log.level] || { tone: "neutral", label: log.level };
              return (
                <LogCard key={log.id}>
                  <LogTop>
                    <Badge $tone={meta.tone} $dot>
                      {meta.label}
                    </Badge>
                    <LogTime>{log.timestamp}</LogTime>
                  </LogTop>
                  <LogAction>{log.action}</LogAction>
                  <LogMeta>
                    <Tag>{log.category}</Tag>
                    <span>👤 {log.user}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>🌐 {log.ip}</span>
                  </LogMeta>
                  <LogDetail>{log.detail}</LogDetail>
                </LogCard>
              );
            })}
            <ListCard>
              <Pagination
                page={currentPage}
                totalPages={totalPages}
                onChange={setPage}
                totalItems={filteredLogs.length}
                pageSize={PAGE_SIZE}
              />
            </ListCard>
          </>
        )}
      </Container>
    </MobileSubNavigator>
  );
}
